/**
 * Reactive online/offline status — the gating primitive for offline mode
 * (`docs/OFFLINE_MODE_PLAN.md` §3.1).
 *
 * On native we use `@capacitor/network`, whose `networkStatusChange` events fire
 * reliably — the WebView's own `online`/`offline` events can FAIL to fire after
 * toggling airplane mode, which left the offline banner stuck on. The `navigator`
 * window events are kept as the web implementation and as a belt-and-suspenders
 * fallback on native.
 *
 * State + listeners are module-level so every consumer shares one source of truth
 * and we never attach more than one set of listeners.
 */
import { ref, readonly, type DeepReadonly, type Ref } from "vue";
import { Capacitor } from "@capacitor/core";

const online = ref(typeof navigator !== "undefined" ? navigator.onLine : true);
const reconnectCallbacks = new Set<() => void>();
let initialized = false;

/** Update connectivity; fire onReconnect callbacks on a genuine offline -> online flip. */
function setOnline(value: boolean) {
  const wasOffline = !online.value;
  online.value = value;
  if (value && wasOffline) {
    reconnectCallbacks.forEach((cb) => {
      try {
        cb();
      } catch (error) {
        console.error("[useNetworkStatus] onReconnect callback failed", error);
      }
    });
  }
}

function ensureListeners() {
  if (initialized || typeof window === "undefined") return;
  window.addEventListener("online", () => setOnline(true));
  window.addEventListener("offline", () => setOnline(false));
  initialized = true;

  // Native: reliable connectivity via @capacitor/network (loaded lazily so the web
  // build / unit tests never touch the plugin).
  if (Capacitor.isNativePlatform()) {
    import("@capacitor/network")
      .then(({ Network }) => {
        Network.getStatus()
          .then((status) => setOnline(status.connected))
          .catch(() => {});
        Network.addListener("networkStatusChange", (status) => setOnline(status.connected));
      })
      .catch(() => {});
  }
}

export interface NetworkStatus {
  /** Reactive connectivity flag. */
  isOnline: DeepReadonly<Ref<boolean>>;
  /** Register a callback to run on every offline -> online transition. Returns an unsubscribe fn. */
  onReconnect: (cb: () => void) => () => void;
}

export function useNetworkStatus(): NetworkStatus {
  ensureListeners();

  function onReconnect(cb: () => void): () => void {
    reconnectCallbacks.add(cb);
    return () => {
      reconnectCallbacks.delete(cb);
    };
  }

  return { isOnline: readonly(online), onReconnect };
}
